import React, { useEffect, useCallback, useMemo } from 'react'
import _ from 'lodash'
import { useNewTranslation } from 'hooks/index'
import { useDispatch, useSelector } from 'react-redux'
import { Box } from 'rebass'
import { theme } from '../../../../../theme'
import SimpleTable from '../SimpleTable'
import DraggableTable from './DraggableTable'
import { SubmissionContainer } from '../../Submissions/SubmissionList'
import { Column } from 'react-table'
import {
    selectSelectedCampaignUuid,
    REGISTRATION_VIEW_ENUM,
    REGISTRATION_STATUS,
    selectIsSelectedCampaignDeadlineOver,
    selectSearchablePlatforms,
} from 'state/review'
import RootHeaderBar from './RootHeaderBar'
import { getPriorityList, clearPriorityList } from 'state/registrations'
import { GLOBAL_MODAL_TYPE, openModalWithProps, ActionModalProps } from 'state/constants'
import { PLATFORM_ENUM } from 'services/api/constants'
import { selectCampaign } from 'state'
import LinkButton from 'components/Shared/Button/LinkButton'
import { getCampaignFollowerRequirements, showDefaultIgPlatform } from 'components/utils/campaign'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import { ProfileCell, ProfileDataProps } from 'components/Shared/MuiTable/Cell/ProfileCell'
import { SocialMediaInfoCell, SocialMediaDataProps } from 'components/Shared/MuiTable/Cell/SocialMediaInfoCell'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'

interface WaitlistTableProps {
    viewType: REGISTRATION_VIEW_ENUM
}

export interface RowProps {
    id: number
    uuid: string
    profile: ProfileDataProps
    socialMediaInfo: SocialMediaDataProps
    igUsername?: string | null
    fbUsername?: string | null
    tiktokUsername?: string | null
    twitterUsername?: string | null
    xiaohongshuUsername?: string | null
    igProfile?: any
    fbProfile?: any
    tiktokProfile?: any
    twitterProfile?: any
    xiaohongshuProfile?: any
    action: ActionModalProps
}

const WaitlistTable: React.FC<WaitlistTableProps> = ({ viewType }) => {
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const campaignUuid = useSelector(selectSelectedCampaignUuid)
    const isSelectedCampaignDeadlineOver = useSelector(selectIsSelectedCampaignDeadlineOver)
    const searchablePlatforms = useSelector(selectSearchablePlatforms)
    const hasIgPlatform = searchablePlatforms.includes(PLATFORM_ENUM.IG)
    const hasFbPlatform = searchablePlatforms.includes(PLATFORM_ENUM.FB)
    const hasTiktokPlatform = searchablePlatforms.includes(PLATFORM_ENUM.TIKTOK)
    const hasTwitterPlatform = searchablePlatforms.includes(PLATFORM_ENUM.TWITTER)
    const hasXiaohongshuPlatform = searchablePlatforms.includes(PLATFORM_ENUM.XIAOHONGSHU)
    const campaign = useSelector(selectCampaign(campaignUuid as string))
    const onOpenActionDetailModal = (actionProps: ActionModalProps): void => {
        dispatch(openModalWithProps({ type: GLOBAL_MODAL_TYPE.INTEREST_REGISTRATION_ACTION_DETAIL, actionProps }))
    }
    const hidePriorityListToolBar = Boolean(isSelectedCampaignDeadlineOver)
    const refreshList = useCallback(() => {
        if (campaignUuid) {
            dispatch(
                getPriorityList({
                    campaignUuid,
                    status: REGISTRATION_STATUS[REGISTRATION_VIEW_ENUM.WAITLISTED],
                    is_priority: true,
                }),
            )
        }
    }, [campaignUuid])
    const hasPlatform = (platform: PLATFORM_ENUM) => {
        return searchablePlatforms.includes(platform) || followerRequirementsPlatform.includes(platform)
    }

    const followerRequirementsPlatform = useMemo(() => getCampaignFollowerRequirements(campaign), [campaign])

    useEffect(() => {
        refreshList()

        return (): void => {
            dispatch(clearPriorityList())
        }
    }, [refreshList])

    const columns: Column<RowProps>[] = useMemo(() => {
        return [
            {
                Header: '#',
                accessor: 'id',
                width: 25,
                maxWidth: 25,
                minWidth: 25,
            },
            {
                Header: newT('submission.table.profile').toString(),
                accessor: 'profile',
                width: 160,
                minWidth: 60,
                Cell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
            },
            {
                Header: newT('submission.table.socialMediaInfo').toString(),
                accessor: 'socialMediaInfo',
                width: 180,
                minWidth: 60,
                Cell: (props: GridRenderCellParams) => <SocialMediaInfoCell {...props} />,
                subFields: [
                    ...(showDefaultIgPlatform(searchablePlatforms, followerRequirementsPlatform)
                        ? [
                              {
                                  field: FIELDS_TYPE.IG_FOLLOWERS,
                                  headerName: newT('submission.table.instagramFollowersLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.IG_USERNAME,
                                  headerName: newT('submission.table.instagramUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(PLATFORM_ENUM.FB)
                        ? [
                              {
                                  field: FIELDS_TYPE.FB_FRIENDS,
                                  headerName: newT('submission.table.facebookFriendsLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.FB_USERNAME,
                                  headerName: newT('submission.table.facebookUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(PLATFORM_ENUM.TIKTOK)
                        ? [
                              {
                                  field: FIELDS_TYPE.TIKTOK_FOLLOWERS,
                                  headerName: newT('submission.table.tikTokFollowersLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.TIKTOK_USERNAME,
                                  headerName: newT('submission.table.tikTokUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(PLATFORM_ENUM.TWITTER)
                        ? [
                              {
                                  field: FIELDS_TYPE.TWITTER_FOLLOWERS,
                                  headerName: newT('submission.table.twitterFollowersLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.TWITTER_USERNAME,
                                  headerName: newT('submission.table.twitterUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(PLATFORM_ENUM.XIAOHONGSHU)
                        ? [
                              {
                                  field: FIELDS_TYPE.XIAOHONGSHU_USERNAME,
                                  headerName: newT('submission.table.XHSUsernameLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.XIAOHONGSHU_FOLLOWERS,
                                  headerName: newT('submission.table.XHSFollowersLabel'),
                              },
                          ]
                        : []),
                ],
            },
            {
                Header: newT('submission.table.actionLabel', 'Action').toString(),
                accessor: 'action',
                width: 80,
                maxWidth: 80,
                minWidth: 80,
                Cell: function getUrlCell(props): JSX.Element {
                    return (
                        <LinkButton
                            href="#"
                            onClick={(): void => onOpenActionDetailModal(props.value)}
                            style={{
                                wordBreak: 'break-all',
                                fontSize: theme.newFontSizes.m,
                                fontWeight: theme.fontWeight.medium,
                            }}
                            value={newT('button.seeDetails', 'See Details')}
                        />
                    )
                },
            },
        ]
    }, [hasFbPlatform, hasIgPlatform, hasTiktokPlatform, hasTwitterPlatform, hasXiaohongshuPlatform])
    return (
        <SubmissionContainer
            flexWrap={'wrap'}
            m={theme.spacing.xxs}
            height={hidePriorityListToolBar ? '1134px' : '1180px'}
        >
            <Box width={1} mt={theme.spacing.xxs}>
                <RootHeaderBar />
            </Box>

            <Box width={1 / 2} sx={{ borderRight: `1px solid ${theme.newColors.neutral200}` }}>
                <SimpleTable viewType={viewType} />
            </Box>
            <Box width={1 / 2} p={theme.spacing.xxs}>
                <DraggableTable columns={columns} hideToolbar={hidePriorityListToolBar} />
            </Box>
        </SubmissionContainer>
    )
}

export default WaitlistTable
